// frontend/admin-portal/src/pages/Categories.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Modal,
  Input,
} from "@fiberhome/shared-ui";
import { categoryService } from "../services/categoryService";

const KIND_VARIANT = {
  passive: "info",
  cable: "warning",
  active: "primary",
  instrument: "success",
  accessory: "default",
};

const KINDS = ["passive", "cable", "active", "instrument", "accessory"];

const EMPTY = {
  name: "",
  slug: "",
  description: "",
  kind: "passive",
  isActive: true,
};

export default function Categories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ kind: "", search: "" });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await categoryService.list({
        withCounts: true,
        isActive: undefined,
      });
      setItems(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalError("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      slug: item.slug || "",
      description: item.description || "",
      kind: item.kind || "passive",
      isActive: item.isActive !== false,
    });
    setModalError("");
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setModalError("");
    try {
      if (editing?.id) await categoryService.update(editing.id, form);
      else await categoryService.create(form);
      setModalOpen(false);
      load();
    } catch (err) {
      setModalError(
        err.response?.data?.message || err.message || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`))
      return;
    try {
      await categoryService.remove(item.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const filtered = items.filter((c) => {
    if (filters.kind && c.kind !== filters.kind) return false;
    if (
      filters.search &&
      !c.name.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;
    return true;
  });

  const columns = [
    {
      key: "name",
      label: "Category",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.slug}
          </div>
        </div>
      ),
    },
    {
      key: "kind",
      label: "Kind",
      render: (r) => (
        <Badge variant={KIND_VARIANT[r.kind] || "default"}>{r.kind}</Badge>
      ),
    },
    {
      key: "productCount",
      label: "Products",
      render: (r) => <strong>{r.productCount ?? 0}</strong>,
    },
    {
      key: "isActive",
      label: "Status",
      render: (r) => (
        <Badge variant={r.isActive ? "success" : "default"}>
          {r.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 180,
      render: (r) => (
        <div className="d-flex gap-1">
          <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => remove(r)}
            disabled={r.productCount > 0}
            title={
              r.productCount > 0
                ? "Cannot delete while products use this category"
                : "Delete"
            }
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Categories"
        subtitle="Group products by type and configure their specification fields"
        breadcrumbs={["Home", "Categories"]}
        actions={
          <Button variant="primary" onClick={openNew}>
            + New Category
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <div className="d-flex gap-2 flex-wrap">
          <input
            className="fh-input"
            placeholder="Search by name..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ maxWidth: 280 }}
          />
          <select
            className="fh-input"
            value={filters.kind}
            onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
            style={{ maxWidth: 180 }}
          >
            <option value="">All kinds</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
      </Card>

      <Card title={`Categories (${filtered.length})`}>
        <Table
          columns={columns}
          data={filtered}
          loading={loading}
          emptyMessage="No categories"
        />
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit: ${editing.name}` : "New Category"}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={save} loading={saving}>
              {editing ? "Save" : "Create"}
            </Button>
          </>
        }
      >
        {modalError && (
          <Alert variant="danger" className="mb-3">
            {modalError}
          </Alert>
        )}

        <Input
          label="Name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Slug"
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })}
          hint="Auto-generated from name if empty"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />

        <label className="fh-input-label">Kind</label>
        <select
          className="fh-input mb-3"
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>

        <label className="fh-checkbox">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          <span>Active</span>
        </label>
      </Modal>
    </>
  );
}
